import React, { useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sector, Site, TaskFilterInputObject } from 'graphql-common';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Table from '@lib/components/Table/Table';
import usePrevious from '@lib/hooks/usePrevious';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  ORDERING_PARAMS,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import useSitesQueryHook from 'utils/fetch/hooks/useSitesQueryHook';
import useSectorsQueryHook from 'utils/fetch/hooks/useSectorsQueryHook';
import { Values } from '@lib/interfaces/form';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import getMonitoringEntity from '../utils/getMonitoringEntity';
import getSitesPerformanceTableColumns from '../utils/getPerformanceTableColumns';
import { MonitoringEntity } from '../enums';
import getStatusKind from '../utils/getStatusKind';

const defaultPaginationState = {
  ...PAGINATION_PARAMS,
  [GetUrlParams.OrderingField]: 'name',
  [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
};

type Props = {
  filterParams: Values;
  taskFilters: TaskFilterInputObject[];
};

function MonitoringPerformance(props: Props) {
  const { filterParams, taskFilters } = props;
  const { t } = useTranslation();
  const orderingState = useState<OrderingParams>({ ...ORDERING_PARAMS });
  const paginationState = useState<PaginationParams>(defaultPaginationState);
  const [orderingParams, setOrderingParams] = orderingState;
  const [paginationParams, setPaginationParams] = paginationState;

  const monitoringEntity = getMonitoringEntity({ filterParams });
  const prevMonitoringEntity = usePrevious(monitoringEntity);

  const {
    collection: sites,
    totalCount: sitesTotalCount,
    loading: sitesLoading,
    refetch: refetchSites,
    error: sitesError,
  } = useSitesQueryHook({
    fetchPolicy: 'no-cache',
    paginationParams: {
      ...paginationParams,
      ...orderingParams,
    },
    skip: monitoringEntity !== MonitoringEntity.Site,
    includeFullData: false,
    includePerformanceDataByTaskDeadlinesMissed: true,
    includePerformanceDataByTaskResults: true,
    taskFilters,
  });

  const {
    collection: sectors,
    totalCount: sectorsTotalCount,
    loading: sectorsLoading,
    refetch: refetchSectors,
    error: sectorsError,
  } = useSectorsQueryHook({
    fetchPolicy: 'no-cache',
    paginationParams: {
      ...paginationParams,
      ...orderingParams,
    },
    skip: monitoringEntity !== MonitoringEntity.Sector,
    includeFullData: false,
    includePerformanceDataByTaskDeadlinesMissed: true,
    includePerformanceDataByTaskResults: true,
    taskFilters,
  });

  const statusKind = getStatusKind({ filterParams });
  const columns = getSitesPerformanceTableColumns({
    t,
    monitoringEntity,
    statusKind,
  });

  useEffect(() => {
    if (prevMonitoringEntity !== monitoringEntity) {
      setPaginationParams({ ...defaultPaginationState });
      setOrderingParams({ ...ORDERING_PARAMS });
    }
  }, [
    prevMonitoringEntity,
    refetchSectors,
    refetchSites,
    setOrderingParams,
    setPaginationParams,
    monitoringEntity,
  ]);

  let title = '';
  let totalCount = 0;
  if (monitoringEntity === MonitoringEntity.Site) {
    title = t('performance-by-site');
    totalCount = sitesTotalCount;
  } else if (monitoringEntity === MonitoringEntity.Sector) {
    title = t('performance-by-sector');
    totalCount = sectorsTotalCount;
  }

  const hasError = !!(sitesError || sectorsError);
  let data: Site[] | Sector[] = [];
  if (monitoringEntity === MonitoringEntity.Site) {
    data = sites;
  } else if (monitoringEntity === MonitoringEntity.Sector) {
    data = sectors;
  }
  const isLoading = sitesLoading || sectorsLoading;

  return (
    <div>
      <DashboardTitleHeader
        bottomSpace="middle"
        title={title}
        titleVariant="h3"
      />
      {hasError ? (
        <EmptyState
          icon={IconName.Cancel}
          text={t('refresh-or-retry-later')}
          title={t('failed-to-load')}
        />
      ) : (
        <Table
          columns={columns}
          data={data}
          id="sites-performance"
          loading={isLoading}
          orderingState={orderingState}
          paginationState={paginationState}
          totalEntries={totalCount}
        />
      )}
    </div>
  );
}

export default memo(MonitoringPerformance);
